import "./dashboard.scss";

import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";

import HelpModal from "@components/HelpModal";
import OverlayPanel from "@components/OverlayPanel";
import { IdentityVerificationConfirmedBannerProvider } from "@contexts/identityVerificationConfirmedBanner/IdentityVerificationConfirmedBannerProvider";
import {
  IdentityVerificationPanelProvider,
  useIdentityVerificationPanel,
} from "@contexts/identityVerificationPanel/IdentityVerificationPanelProvider";
import Menu from "@elements/Menu/Menu";
import { useTitle } from "@hooks/useTitle";
import LegacyAdminEntry from "@pages/AdminDashboard/LegacyAdminEntry";
import { rebuiltAdminRedirectFor } from "@pages/AdminDashboard/adminRoutes";
import MenuSvg from "@img/menu.svg";
import LogoSvg from "@img/RunwayLogo.svg";
import * as casesActions from "@store/cases/casesActions";
import { RESTORE_CONNECTION } from "@store/socket/socketActions";
import { GET_AFFILIATE_PROFILE } from "@store/user/userActions";
import { Case } from "@types-runway/case";
import { RootEntity, RootState } from "@types-runway/state";
import { User } from "@types-runway/user";
import { LANDING_PAGE } from "@utils/constantsEnv";

import Account from "./Components/Account/Account";
import Cases from "./Components/Cases/Cases";
import IdentityVerificationBanner from "./Components/IdentityVerification/IdentityVerificationBanner";
import IdentityVerificationPanelContent from "./Components/IdentityVerification/IdentityVerificationPanelContent";
import Messages from "./Components/Messages/Messages";
import Referrals from "./Components/Referrals/Referrals";

// Bridges the Provider's context to OverlayPanel's open/close props — must be
// mounted inside IdentityVerificationPanelProvider to read the shared state.
const IdentityVerificationOverlay = () => {
  const { isOpen, close } = useIdentityVerificationPanel();
  return <OverlayPanel open={isOpen} close={close} child={IdentityVerificationPanelContent} />;
};

const Dashboard = () => {
  const [menu, setMenu] = useState(false);
  const [showRewardButton, setShowRewardButton] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const hasNotification = useSelector<RootState, boolean>(
    (state) => state.ui.notification?.message
  );
  const user = useSelector<RootState, RootEntity<User>>((state) => state.user);
  const cases = useSelector<RootState, RootEntity<Case>[]>((state) => state.case.cases.data);
  // User rewards will be active after 10 days from initial purchase.
  const TEN_DAYS_IN_MS = 1000 * 60 * 60 * 24 * 10;
  const [userRewardsActivated, setUserRewardsActivated] = useState(false);

  useTitle("Dashboard");

  // Effect 1: Admin redirect (run once) to the rebuilt admin (XCUT-R29). The
  // legacy admin (XCUT-R30) and a visit chosen from the rebuilt admin (XCUT-R20)
  // stay where they are. It replaces the history entry, so Back from the rebuilt
  // admin does not land on an address that redirects again (XCUT-R31).
  useEffect(() => {
    if (!user.data.isAdmin) return;

    const address = rebuiltAdminRedirectFor(location);
    if (address) history.replace(address);
  }, [user.data.isAdmin]);

  // Effect 2: Calculate rewards activation (run when cases change)
  useEffect(() => {
    if (cases.length > 0) {
      const validDates = cases
        .filter((c) => c.data?.createdAt && c.data.paid)
        .map((c) => new Date(c.data.createdAt as string));

      if (validDates.length > 0) {
        const earliestPurchase = validDates.sort((a, b) => a.getTime() - b.getTime())[0];
        setUserRewardsActivated(
          new Date().getTime() - earliestPurchase.getTime() >= TEN_DAYS_IN_MS
        );
      }
    }
  }, [cases]);

  useEffect(() => {
    if (user.data.id) {
      dispatch({
        type: casesActions.REQUEST_CASES,
        userId: user.data.id,
      });
      dispatch({
        type: RESTORE_CONNECTION,
        authToken: localStorage.getItem("token"),
      });
      if (!user.data.affiliateProfile) {
        dispatch({
          type: GET_AFFILIATE_PROFILE,
        });
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (user.data.affiliateProfile) {
      const validAffiliate = user.data.affiliateProfile.conversions < 5;
      setShowRewardButton(validAffiliate && userRewardsActivated);
    }
  }, [user.data.affiliateProfile]);

  return (
    <div className="dashboard">
      <div className="dashboard-menu" style={{ left: menu ? "0" : "-100%" }}>
        <Menu
          setMenu={setMenu}
          userId={user.data.id}
          hasNotification={hasNotification}
          showRewardButton={showRewardButton}
        />
      </div>
      <div className="dashboard-content">
        <header className="dashboard-content-header">
          <img
            className={"mobile-menu"}
            src={MenuSvg}
            alt=""
            style={{ cursor: "pointer" }}
            onClick={() => setMenu((prev) => !prev)}
          />
          {hasNotification && <div className="notification"></div>}
          <a href={LANDING_PAGE} target="blank">
            <img className={"mobile-logo"} src={LogoSvg} alt="" style={{ height: "38px" }} />
          </a>
          <div className="dashboard-content-header-icons">
            <HelpModal />
          </div>
        </header>
        <IdentityVerificationPanelProvider>
          <IdentityVerificationConfirmedBannerProvider>
            <IdentityVerificationBanner />
            <Switch>
              <Route exact path="/dashboard/" component={Cases} />
              <Route exact path="/dashboard/cases" component={Cases} />
              <Route exact path="/dashboard/cases?referral=true" component={Cases} />
              <Route exact path="/dashboard/cases?cart=true" component={Cases} />
              <Route exact path="/dashboard/account" component={Account} />
              <Route exact path="/dashboard/messages" component={Messages} />
              <Route exact path="/dashboard/messages/:caseId" component={Messages} />
              <Route exact path="/dashboard/referrals" component={Referrals} />
              <Route exact path="/dashboard/admin" component={LegacyAdminEntry} />
            </Switch>
            <IdentityVerificationOverlay />
          </IdentityVerificationConfirmedBannerProvider>
        </IdentityVerificationPanelProvider>
      </div>
    </div>
  );
};

export default Dashboard;
